import * as _ from 'lodash';
import React, { useMemo, useState, useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import moment from 'moment';
import PropTypes from 'prop-types';
import { Icon } from 'semantic-ui-react';
import { useHistory } from 'react-router-dom';
import { isTaskRegular } from '../../../HelperMethods/TaskUtilities';
import cardStyles from '../Card.module.css';
import styles from './AssigneeCard.module.css';
import { ReactComponent as SkillIcon } from '../../../icons/skill-icon.svg';
import { ReactComponent as AvailabilityIcon } from '../../../icons/availability-icon.svg';
import { ReactComponent as CertificationIcon } from '../../../icons/certification-icon.svg';
import { taskIconSvg } from '../../map/constants';
// eslint-disable-next-line import/no-cycle
import TaskBox from '../taskBox/TaskBox';
import {
  getAvailabilityLabelForUserAndSelectedDate,
  determineIfUserHasAccessToResource,
} from '../../../HelperMethods/UserUtilities';
import { ENTITY_ACCESS_TYPE, RESOURCES, RESOURCE_TYPE } from '../../../HelperMethods/role-resource-constants';
import { CONTAINERS } from '../cardNavigationComponent/cardNavigationRedux';
import { isDateInBetweenDateRange, parseJSONStringToObject } from '../../../HelperMethods/UtilityFunctions';
import { DD_MM_YYYY_FORMAT } from '../../../HelperMethods/constants';
import ApiService from '../../../service/apiService';

const EVENT_TYPE_MAP = {
  EXIT: 'Exit',
  ENTER: 'Enter',
  VISIT: 'Visit',
};

const AssigneeCard = React.memo(function AssigneeCard({ assigneeId, container }) {
  const allAssignees = useSelector((state) => state.taskViewPageReducer.users.items);
  const dispatch = useDispatch();
  const loggedInUser = useSelector((state) => state.appReducer.loggedInUser);
  const isLocationServiceEnabled = useSelector(
    (state) =>
      state.appReducer.settings.featureFlags.isAWSLocationEnabled ||
      state.appReducer.settings.featureFlags.useHypertrackWithAI
  );

  const settings = useSelector((state) => state.appReducer.settings);
  const packagesInfo = useSelector((state) => state.appReducer.packagesInfo);
  const assignee = useMemo(() => allAssignees.find((u) => u.id === assigneeId), [allAssignees, assigneeId]);
  const history = useHistory();
  const {
    tasks: { items: allTasks },
    selectedDateRange: { endDate, startDate },
  } = useSelector((state) => state.taskViewPageReducer);
  const editPermission = determineIfUserHasAccessToResource({
    resourceType: RESOURCE_TYPE.ENTITY,
    resource: RESOURCES.ENTITY.USER,
    entityAccessType: ENTITY_ACCESS_TYPE.UPDATE,
    user: loggedInUser,
    packageName: settings?.package,
    packagesInfo,
  });
  const editAssignee = (id) => {
    dispatch({ type: 'USER_MANAGEMENT_PAGE_RETURN_TASK_PAGE_STATE' });
    history.push({ pathname: `/usermanagement/singleedit/${id}`, selectedUser: assignee });
  };

  const tasksAssignedToAssignee = useMemo(() => {
    return allTasks
      .filter(
        (t) =>
          isTaskRegular(t) &&
          isDateInBetweenDateRange({ date: moment(t.startTime).format('YYYY-MM-DD'), startDate, endDate }) &&
          t.assigneeIds
            .filter((a) => !a.isDeleted)
            .map((a) => a.assigneeId)
            .includes(assigneeId)
      )
      .sort((task1, task2) => new Date(task1.startTime).getTime() - new Date(task2.startTime).getTime());
  }, [allTasks, startDate, endDate, assigneeId]);
  if (!assignee) return <div className={cardStyles.card}>Assignee Not Available</div>;
  return (
    <div className={cardStyles.card}>
      <div className={cardStyles.cardHeader}>
        <div className={cardStyles.taskHeaderLines} style={{ paddingLeft: '48px', display: 'flex' }}>
          <div className={cardStyles.mainHeader}>{`${assignee.firstName} ${assignee.lastName}`}</div>
          <div style={{ marginLeft: '10px' }}>
            {editPermission && (
              <div
                className="p-cursor"
                onClick={() => {
                  editAssignee(assigneeId);
                }}
              >
                <Icon name="edit outline" />
              </div>
            )}
          </div>
        </div>
      </div>
      <div className={cardStyles.cardContent}>
        <AssigneeDetails assignee={assignee} isLocationServiceEnabled={isLocationServiceEnabled} />
        <TaskDetails tasks={tasksAssignedToAssignee} container={container} />
      </div>
    </div>
  );
});

AssigneeCard.propTypes = {
  container: PropTypes.oneOf(Object.values(CONTAINERS)).isRequired,
  assigneeId: PropTypes.string.isRequired,
};

function AssigneeDetails({ assignee, isLocationServiceEnabled }) {
  return (
    <>
      {isLocationServiceEnabled && <LocationStatusDetails assignee={assignee} />}
      <DeviceMetadataDetails assignee={assignee} />
      <SkillLevelDetails skillLevel={assignee.skillLevel} />
      <AvailabilityDetails assignee={assignee} />
      <CertificationDetails certificates={assignee.certificates} />
    </>
  );
}

AssigneeDetails.propTypes = {
  assignee: PropTypes.shape({
    skillLevel: PropTypes.object,
    certificates: PropTypes.array,
  }).isRequired,
  isLocationServiceEnabled: PropTypes.bool,
};

function SkillLevelDetails({ skillLevel }) {
  if (!skillLevel) return null;
  return (
    <div className={cardStyles.row} style={{ background: 'rgba(200, 244, 240, 0.16)' }}>
      <div className={cardStyles.rowIcon}>
        <SkillIcon />
      </div>
      <div className={cardStyles.rowTitle}>Skill Level</div>
      <div className={cardStyles.rowDetails}>{skillLevel?.value ?? ''}</div>
    </div>
  );
}

SkillLevelDetails.propTypes = {
  skillLevel: PropTypes.shape({ value: PropTypes.number }),
};
SkillLevelDetails.defaultProps = {
  skillLevel: null,
};

function DeviceMetadataDetails({ assignee }) {
  if (!assignee || !assignee.deviceMetadata) return null;
  const deviceMetadata = parseJSONStringToObject(assignee.deviceMetadata);
  if (!deviceMetadata || !Object.keys(deviceMetadata).length) return null;
  return (
    <div className={cardStyles.row} style={{ background: 'rgba(200, 244, 240, 0.16)' }}>
      <div className={cardStyles.rowIcon}>
        <SkillIcon />
      </div>
      <div className={cardStyles.rowTitle}>Location Permission</div>
      <div className={cardStyles.rowDetails}>{deviceMetadata?.LocationPermission ?? '-'}</div>
    </div>
  );
}

function AvailabilityDetails({ assignee }) {
  const displayLabel = getAvailabilityLabelForUserAndSelectedDate(assignee, moment().format('YYYY-MM-DD'));
  return (
    <div className={cardStyles.row}>
      <div className={cardStyles.rowIcon}>
        <AvailabilityIcon />
      </div>
      <div className={cardStyles.rowTitle}>Availability on {moment().format(DD_MM_YYYY_FORMAT)}</div>
      <div className={cardStyles.rowDetails}>{displayLabel}</div>
    </div>
  );
}

AvailabilityDetails.propTypes = {
  assignee: PropTypes.shape({ id: PropTypes.string.isRequired }).isRequired,
};

function CertificationDetails({ certificates }) {
  if (!certificates || certificates.length === 0) return null;
  return (
    <>
      <div className={cardStyles.row} style={{ background: 'rgba(200, 244, 240, 0.16)' }}>
        <div className={cardStyles.rowIcon}>
          <CertificationIcon />
        </div>
        <div className={cardStyles.rowTitle}>Certifications</div>
      </div>
      <div style={{ padding: '0px 28px' }}>
        {certificates.map((certificate) => (
          <div className={styles.singleRowCertificate} key={certificate.id}>
            {certificate.name}
          </div>
        ))}
      </div>
    </>
  );
}

function LocationStatusDetails({ assignee }) {
  const [geofenceInfo, setGeofenceInfo] = useState({});
  const [locationInfo, setLocationInfo] = useState({});

  useEffect(() => {
    async function fetchInfo() {
      const response = await ApiService.getLastLocationAndEventInfoForUser(assignee.id);
      const success = _.get(response, 'data.rpcService.success');
      if (success) {
        const output = _.get(response, 'data.rpcService.output');
        const { event, location } = JSON.parse(output);
        if (event) {
          const { eventType, stopName, sampleTime } = event;
          setGeofenceInfo({
            stopName,
            dateTime: new Date(sampleTime).toLocaleString(),
            eventType: EVENT_TYPE_MAP[eventType],
          });
        }
        if (location) {
          const { receivedTime } = location;
          if (receivedTime) {
            setLocationInfo({ dateTime: new Date(receivedTime).toLocaleString() });
          }
        }
      }
    }
    fetchInfo();
  }, []);

  return (
    <>
      {geofenceInfo && (
        <div>
          <div className={cardStyles.row} style={{ background: 'rgba(200, 244, 240, 0.16)' }}>
            <div className={cardStyles.rowIcon}>
              <CertificationIcon />
            </div>
            <div className={cardStyles.rowTitle}>Last Geofence Visit Details</div>
          </div>
          <div
            className={cardStyles.row}
            style={{ background: 'rgba(200, 244, 240, 0.16)', padding: '0px 15px 0 49px' }}
          >
            <div className={cardStyles.rowTitle} style={{ minWidth: '100px' }}>
              Stop
            </div>
            <div className={cardStyles.rowDetails}>{geofenceInfo.stopName}</div>
          </div>
          <div
            className={cardStyles.row}
            style={{ background: 'rgba(200, 244, 240, 0.16)', padding: '0px 15px 0 49px' }}
          >
            <div className={cardStyles.rowTitle} style={{ minWidth: '100px' }}>
              Type
            </div>
            <div className={cardStyles.rowDetails}>{geofenceInfo.eventType}</div>
          </div>
          <div
            className={cardStyles.row}
            style={{ background: 'rgba(200, 244, 240, 0.16)', padding: '0px 15px 0 49px' }}
          >
            <div className={cardStyles.rowTitle} style={{ minWidth: '100px' }}>
              Date & Time
            </div>
            <div className={cardStyles.rowDetails}>{geofenceInfo.dateTime}</div>
          </div>
        </div>
      )}
      {locationInfo && (
        <div className={cardStyles.row} style={{ background: 'rgba(200, 244, 240, 0.16)' }}>
          <div className={cardStyles.rowIcon}>
            <SkillIcon />
          </div>
          <div className={cardStyles.rowTitle} style={{ minWidth: '100px' }}>
            Last Location Ping
          </div>
          <div className={cardStyles.rowDetails}>{locationInfo.dateTime || '-'}</div>
        </div>
      )}
    </>
  );
}

LocationStatusDetails.propTypes = {
  assignee: PropTypes.shape({ id: PropTypes.string.isRequired }).isRequired,
};

CertificationDetails.propTypes = {
  certificates: PropTypes.arrayOf(
    PropTypes.shape({
      id: PropTypes.string.isRequired,
      name: PropTypes.string.isRequired,
    })
  ),
};
CertificationDetails.defaultProps = {
  certificates: null,
};

function TaskDetails({ tasks, container }) {
  if (!tasks || tasks.length === 0) return null;
  return (
    <>
      <div className={cardStyles.row} style={{ background: 'rgba(200, 244, 240, 0.16)' }}>
        <div className={cardStyles.rowIcon}>
          <img
            alt="task-icon"
            src={`data:image/svg+xml;charset=utf-8,${encodeURIComponent(taskIconSvg)}`}
            style={{ height: '12px' }}
          />
        </div>
        <div className={cardStyles.rowTitle}>Tasks</div>
      </div>
      {tasks.map((task) => (
        <TaskBox key={task.id} task={task} container={container} />
      ))}
    </>
  );
}

TaskDetails.propTypes = {
  tasks: PropTypes.arrayOf(PropTypes.shape({ id: PropTypes.string.isRequired })),
  container: PropTypes.oneOf(Object.values(CONTAINERS)).isRequired,
};

TaskDetails.defaultProps = {
  tasks: null,
};

export default AssigneeCard;
